import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, loginUser, registerUser, logoutUser, startCheckout, getConfig } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [billingLive, setBillingLive] = useState(true);
  const [freeUntil, setFreeUntil] = useState(null);
  const [freeAnonQuestions, setFreeAnonQuestions] = useState(2);

  useEffect(() => {
    getConfig()
      .then((c) => {
        setBillingLive(!!c.billing_live);
        setFreeUntil(c.free_until || null);
        if (typeof c.free_anon_questions === "number") setFreeAnonQuestions(c.free_anon_questions);
      })
      .catch(() => setBillingLive(true));
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const data = await loginUser({ email, password });
    setUser(data.user);
    setAuthOpen(false);
    return data.user;
  };

  const register = async (name, email, password) => {
    const data = await registerUser({ name, email, password });
    setUser(data.user);
    setAuthOpen(false);
    return data.user;
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
  };

  const goToCheckout = async () => {
    const { checkout_url } = await startCheckout();
    window.location.assign(checkout_url);
  };

  // A feature is unlocked during beta (billing not live) OR when the user has premium
  // (which, during the global free period, is true for every registered user).
  const featuresUnlocked = !billingLive || !!user?.premium;
  const freePeriodActive = !!freeUntil && new Date(freeUntil).getTime() > Date.now();

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, authOpen, setAuthOpen, upgradeOpen, setUpgradeOpen, login, register, logout, checkAuth, goToCheckout, billingLive, featuresUnlocked, freeUntil, freePeriodActive, freeAnonQuestions }}
    >
      {children}
    </AuthContext.Provider>
  );
};
